import { CreatedFrom } from 'src/modules/homeownerApp/static_values';
import type {
  AiDisplayImageFragment,
  AiGeneratedImageFragment,
  AiOriginalImage,
} from 'src/shared/graphql/generated';

import exterior from 'assets/img/homeownerApp/samples/exterior/exteriorSample.jpg';
import interior from 'assets/img/homeownerApp/samples/interior/interiorSample.jpg';

import { exteriorSamples } from './exteriorSamples';
import { interiorSamples } from './interiorSamples';

export interface SampleOriginalImage
  extends Omit<AiOriginalImage, 'id' | 'aiGeneratedImages'> {
  isSample: boolean;
  sampleId: string;
  sampleGeneratedImages: SampleGeneratedImage[];
  sampleDisplayImages: SampleDisplayImage[];
}

export interface SampleGeneratedImage
  extends Omit<AiGeneratedImageFragment, 'id' | 'aiOriginalImageId'> {
  id?: number;
  fromSample: boolean;
  sampleId: string;
  sampleOriginalImageId: string;
  getSampleOriginalImage: () => SampleOriginalImage | undefined;
}

export interface SampleDisplayImage
  extends Omit<
    AiDisplayImageFragment,
    'id' | 'aiOriginalImageId' | 'aiGeneratedImage'
  > {
  sampleId: string;
  sampleOriginalImageId: string;
  aiGeneratedImage: SampleGeneratedImage;
  getSampleOriginalImage: () => SampleOriginalImage | undefined;
}

export interface ISamplePath {
  style: string;
  jpeg: string;
}

// Intentionally setting up a circular reference so we can retrieve the
// sample original image directly from the sample generated images
// eslint-disable-next-line import/no-mutable-exports
let sampleImages: SampleOriginalImage[] = [];

const buildSampleGeneratedImage = (
  id: number | string,
  spaceType: string,
  sampleOriginalImageId: string,
  { style, jpeg }: ISamplePath,
) => ({
  sampleOriginalImageId,
  getSampleOriginalImage: () =>
    sampleImages.find(({ sampleId }) => sampleId === sampleOriginalImageId),
  favorite: false,
  generatedImagePath: jpeg,
  sampleId: `${spaceType}-generated-sample-${id}`,
  imagePath: jpeg,
  spaceType,
  status: 'done',
  style,
  fromSample: false,
});

const generateImages = (
  samples: ISamplePath[],
  sampleOriginalImageId: string,
  spaceType: string,
): SampleGeneratedImage[] =>
  samples.map((sample, index) =>
    buildSampleGeneratedImage(
      index + 1,
      spaceType,
      sampleOriginalImageId,
      sample,
    ),
  );

const generateDisplayImages = (
  samples: ISamplePath[],
  sampleOriginalImageId: string,
  spaceType: string,
  originalSamplePath: ISamplePath,
): SampleDisplayImage[] => [
  {
    createdFrom: CreatedFrom.ORIGINAL,
    sampleId: `display--${sampleOriginalImageId}`,
    sampleOriginalImageId,
    aiGeneratedImage: buildSampleGeneratedImage(
      `-${sampleOriginalImageId}`,
      spaceType,
      sampleOriginalImageId,
      originalSamplePath,
    ),
    getSampleOriginalImage: () =>
      sampleImages.find(({ sampleId }) => sampleId === sampleOriginalImageId),
  },
  ...samples.map((sample, index) => {
    const id = index + 1;

    return {
      createdFrom: CreatedFrom.STYLE,
      sampleId: `display--${spaceType}-sample-${id}`,
      sampleOriginalImageId,
      style: sample.style,
      aiGeneratedImage: buildSampleGeneratedImage(
        id,
        spaceType,
        sampleOriginalImageId,
        sample,
      ),
      getSampleOriginalImage: () =>
        sampleImages.find(({ sampleId }) => sampleId === sampleOriginalImageId),
    } satisfies SampleDisplayImage;
  }),
];

const exteriorSampleGeneratedImages: SampleGeneratedImage[] = generateImages(
  exteriorSamples,
  'exterior-sample-1',
  'exterior',
);

const interiorSampleGeneratedImages: SampleGeneratedImage[] = generateImages(
  interiorSamples,
  'interior-sample-1',
  'kitchen',
);

const exteriorSampleDisplayImages: SampleDisplayImage[] = generateDisplayImages(
  exteriorSamples,
  'exterior-sample-1',
  'exterior',
  { style: 'My Upload', jpeg: exterior },
);

const interiorSampleDisplayImages: SampleDisplayImage[] = generateDisplayImages(
  interiorSamples,
  'interior-sample-1',
  'kitchen',
  { style: 'My Upload', jpeg: interior },
);

const sampleDisplayImages = [
  ...exteriorSampleDisplayImages,
  ...interiorSampleDisplayImages,
];

const getSampleDisplayImage = (sampleId: string) =>
  sampleDisplayImages.find(sample => sample.sampleId === sampleId);

sampleImages = [
  {
    sampleGeneratedImages: exteriorSampleGeneratedImages,
    sampleDisplayImages: exteriorSampleDisplayImages,
    sampleId: 'exterior-sample-1',
    image: exterior,
    imagePath: exterior,
    inputPath: '',
    orientation: 'vertical',
    originalImagePath: exterior,
    spaceType: 'exterior',
    isSample: false,
  },
  {
    sampleGeneratedImages: interiorSampleGeneratedImages,
    sampleDisplayImages: interiorSampleDisplayImages,
    sampleId: 'interior-sample-1',
    image: interior,
    imagePath: interior,
    inputPath: '',
    orientation: 'vertical',
    originalImagePath: interior,
    spaceType: 'kitchen',
    isSample: false,
  },
];

export { getSampleDisplayImage, sampleImages };
